import type { QueryClient } from '@tanstack/react-query';
import { createQuery } from 'react-query-kit';
import { type ApiError } from '../ApiError';
import { BaseApi } from '../BaseApi';
import { type FeaturesCriteria } from './FeaturesCriteria';
import { type FeatureSummary } from './FeatureSummary';
import { type Property } from './Property';
import { type PropertyApi } from './PropertyApi';

interface RawFeatureSummary extends Omit<FeatureSummary, 'createdAt' | 'updatedAt'> {
  readonly createdAt: string;
  readonly updatedAt: string;
}

export class DefaultPropertyApi extends BaseApi implements PropertyApi {
  public constructor(queryClient: QueryClient) {
    super(queryClient);
  }

  public readonly id = (id: number) => ({
    features: createQuery<FeatureSummary[], FeaturesCriteria, ApiError>({
      queryKey: ['property/features', id],
      fetcher: async (criteria) => {
        const queryParams = new URLSearchParams();
        criteria.categoryIds.forEach((categoryId) => {
          queryParams.append('category-id', categoryId.toString());
        });
        if (criteria.text.trim().length > 0) {
          queryParams.append('text', criteria.text);
        }
        const endpoint = `/api/property/${id.toString()}/features?${queryParams.toString()}`;
        const rawFeatures = await this.doGet<RawFeatureSummary[]>(endpoint);
        return rawFeatures.map((raw) => ({
          ...raw,
          createdAt: new Date(raw.createdAt),
          updatedAt: new Date(raw.updatedAt),
        }));
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    get: createQuery<Property, void, ApiError>({
      queryKey: ['property', id],
      fetcher: async () => {
        return await this.doGet<Property>(`/api/property/${id.toString()}`);
      },
    }),
  });
}
